import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { ConfBar } from "../components/Bits";
import { HRLogChart } from "../components/SearchSuggest";
import { Star, Trash2 } from "lucide-react";

function WatchHRLog({ id }) {
  const { data } = useQuery({ queryKey: ["hrLog", String(id)], queryFn: () => api.get(`/hr-log/${id}`).then((r) => r.data) });
  if (!data) return <p className="text-[10px] text-neutral-600 animate-pulse mt-2">Loading HR log...</p>;
  return (
    <div className="mt-3 border-t border-white/10 pt-2" data-testid={`watchlist-hrlog-${id}`}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">HRs — Last 15 Games</div>
      <HRLogChart log={data} />
    </div>
  );
}

export default function Watchlist() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["watchlist"], queryFn: () => api.get("/watchlist").then((r) => r.data) });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/watchlist/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["watchlist"] }); toast.success("Removed"); },
  });
  const players = data?.players || [];

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Watch<span className="gold-text">list</span></h1>
        <p className="text-neutral-500 text-sm mt-1">Players you're monitoring.</p>
      </div>

      {players.length === 0 ? (
        <div className="card-surface rounded-xl p-10 text-center fade-up-1" data-testid="watchlist-empty">
          <Star className="mx-auto text-neutral-700 mb-3" size={28} />
          <p className="text-neutral-400">Nothing on the watch yet.</p>
          <Link to="/research" data-testid="watchlist-goto-research" className="inline-block mt-3 text-[#a855f7] hover:underline text-sm font-semibold">Browse the Research Center →</Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 fade-up-1">
          {players.map((p) => (
            <div key={p.mlb_id} className="card-surface hover:border-[#a855f7]/40 rounded-lg p-5 transition-colors duration-200" data-testid={`watchlist-card-${p.mlb_id}`}>
              <div className="flex items-center gap-3">
                <img src={p.headshot} alt="" className="w-12 h-12 rounded-full object-cover bg-white/5 border border-white/10" />
                <div className="flex-1 min-w-0">
                  <Link to={`/research/${p.mlb_id}`} className="font-semibold text-white hover:text-[#a855f7] truncate block transition-colors duration-200">{p.name}</Link>
                  <div className="text-xs text-neutral-500">{p.team} · vs {p.matchup.pitcher}</div>
                </div>
                <button onClick={() => remove.mutate(p.mlb_id)} data-testid={`watchlist-remove-${p.mlb_id}`} className="text-neutral-600 hover:text-red-400 transition-colors duration-200"><Trash2 size={16} /></button>
              </div>
              <div className="mt-3 font-data text-xs text-neutral-400">HR prob {p.stats.hr_probability}% · Barrel {p.stats.barrel_pct}% · Edge <span className={p.odds.edge > 0 ? "text-[#22c55e]" : "text-red-400"}>{p.odds.edge > 0 ? "+" : ""}{p.odds.edge}%</span></div>
              <div className="mt-2"><ConfBar value={p.confidence} /></div>
              <WatchHRLog id={p.mlb_id} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
